import { useEffect, useRef } from 'react';
import { notifyBackIntentChange } from './backIntentBus.js';

const stack = [];

function onDocumentKeydown(e) {
  if (e.key !== 'Escape') return;
  if (stack.length === 0) return;
  e.preventDefault();
  e.stopPropagation();
  stack[stack.length - 1]();
}

let listenerAttached = false;

function ensureListener() {
  if (listenerAttached) return;
  if (typeof document === 'undefined') return;
  document.addEventListener('keydown', onDocumentKeydown, true);
  listenerAttached = true;
}

function removeListenerIfEmpty() {
  if (stack.length === 0 && listenerAttached && typeof document !== 'undefined') {
    document.removeEventListener('keydown', onDocumentKeydown, true);
    listenerAttached = false;
  }
}

export function getEscapeCloseStackSize() {
  return stack.length;
}

export function invokeTopEscapeClose() {
  if (stack.length === 0) return false;
  stack[stack.length - 1]();
  return true;
}

export function resetEscapeCloseStackForTests() {
  stack.length = 0;
  removeListenerIfEmpty();
}

export function registerEscapeCloseHandler(onClose) {
  stack.push(onClose);
  ensureListener();
  notifyBackIntentChange();
  return () => {
    const i = stack.lastIndexOf(onClose);
    if (i !== -1) stack.splice(i, 1);
    removeListenerIfEmpty();
    notifyBackIntentChange();
  };
}

/**
 * Registers an Escape handler while mounted and `enabled`.
 * Nested overlays: the most recently mounted registration runs first (LIFO).
 */
export function useRegisterTopEscapeClose(onClose, enabled = true) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!enabled) return undefined;
    const entry = () => {
      const fn = onCloseRef.current;
      if (typeof fn === 'function') fn();
    };
    return registerEscapeCloseHandler(entry);
  }, [enabled]);
}
